import { useState } from "react";
import { Wine, Coffee, Utensils, GlassWater, Smile, Sprout, WheatOff, Sandwich, Sparkles } from "lucide-react";
import { BAR_MENU, FOOD_MENU, IMAGES } from "../lib/data";

const TABS = [
  { id: "bar", label: "Bar & Cocktails", icon: Wine },
  { id: "mocktails", label: "Mocktails", icon: Sparkles },
  { id: "breakfast", label: "Breakfast", icon: Coffee },
  { id: "lunch", label: "Lunch", icon: Utensils },
  { id: "dinner", label: "Dinner", icon: GlassWater },
  { id: "platters", label: "Platters", icon: Sandwich },
  { id: "kids", label: "Kids", icon: Smile },
  { id: "vegan", label: "Vegan", icon: Sprout },
  { id: "glutenFree", label: "Gluten-Free", icon: WheatOff },
];

const TAG_LABELS = {
  VE: "Vegan",
  VG: "Vegetarian",
  GF: "Gluten-Free",
  "GF*": "Gluten-Free on request",
};

function Badges({ tags }) {
  if (!tags || tags.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5 mt-2">
      {tags.map((t) => (
        <span
          key={t}
          title={TAG_LABELS[t] || t}
          className="text-[0.58rem] tracking-[0.22em] uppercase bg-pink-soft text-navy px-2 py-0.5"
          data-testid={`tag-${t}`}
        >
          {t}
        </span>
      ))}
    </div>
  );
}

function Line({ name, price, desc, tags }) {
  return (
    <div className="flex items-baseline gap-4 py-4 border-b border-[color:var(--am-line)]">
      <div className="flex-1">
        <div className="font-serif-display text-xl text-navy leading-tight">{name}</div>
        {desc && (
          <div className="text-sm text-[color:var(--am-muted)] mt-1 leading-relaxed">{desc}</div>
        )}
        <Badges tags={tags} />
      </div>
      <div className="font-serif-display text-xl text-navy whitespace-nowrap">${price}</div>
    </div>
  );
}

function Section({ title, items }) {
  return (
    <div>
      <div className="overline mb-4">{title}</div>
      <div>
        {items.map((it, i) => (
          <Line key={i} {...it} />
        ))}
      </div>
    </div>
  );
}

export default function MenuPage() {
  const [tab, setTab] = useState("bar");

  return (
    <div data-testid="menu-page" className="bg-sand">
      <section className="pt-28 pb-12 max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="overline mb-5">The Menu</div>
        <h1 className="font-serif-display font-light text-5xl md:text-6xl lg:text-7xl text-navy leading-[1.02] max-w-4xl">
          A small kitchen. A stocked bar.<br />A long afternoon ahead.
        </h1>
        <p className="mt-6 text-[color:var(--am-muted)] max-w-2xl leading-relaxed">
          Our onboard menu leans coastal, bright, and a touch indulgent. All
          items can be added at checkout or pre-ordered for your charter. Dietary
          needs happily accommodated.
        </p>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 pb-24">
        <div className="flex flex-wrap gap-2 border-b border-[color:var(--am-line)] mb-12">
          {TABS.map((t) => {
            const Active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 px-5 py-3 text-xs tracking-[0.22em] uppercase font-medium transition-colors border-b-2 -mb-px ${
                  Active
                    ? "border-navy text-navy"
                    : "border-transparent text-[color:var(--am-muted)] hover:text-navy"
                }`}
                data-testid={`menu-tab-${t.id}`}
              >
                <t.icon size={15} strokeWidth={1.5} />
                {t.label}
              </button>
            );
          })}
        </div>

        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7 space-y-14">
            {tab === "bar" && (
              <>
                <Section title="Signature Cocktails" items={BAR_MENU.signatures} />
                <Section title="Wines by the Glass" items={BAR_MENU.wines} />
                <Section title="Bottles" items={BAR_MENU.bottles} />
                <Section title="Beer & Non-Alcoholic" items={BAR_MENU.beer_nonalc} />
              </>
            )}
            {tab === "mocktails" && <Section title="House Mocktails — Zero Proof, Full Flavour" items={BAR_MENU.mocktails} />}
            {tab === "breakfast" && <Section title="Breakfast at Anchor" items={FOOD_MENU.breakfast} />}
            {tab === "lunch" && <Section title="Lunch on the Water" items={FOOD_MENU.lunch} />}
            {tab === "dinner" && <Section title="Dinner Under the Stars" items={FOOD_MENU.dinner} />}
            {tab === "platters" && <Section title="Shareable Platters" items={FOOD_MENU.platters} />}
            {tab === "kids" && <Section title="Little Mariners' Menu" items={FOOD_MENU.kids} />}
            {tab === "vegan" && <Section title="Plant-Based & Vegan" items={FOOD_MENU.vegan} />}
            {tab === "glutenFree" && <Section title="Gluten-Free" items={FOOD_MENU.glutenFree} />}
          </div>

          <div className="lg:col-span-5">
            <div className="sticky top-28">
              <div className="img-zoom aspect-[4/5] overflow-hidden mb-6">
                <img
                  src={
                    tab === "bar"
                      ? IMAGES.cocktail
                      : tab === "mocktails"
                      ? IMAGES.mocktailGlass
                      : tab === "breakfast"
                      ? IMAGES.breakfast
                      : tab === "lunch"
                      ? IMAGES.lunchPlate
                      : tab === "dinner"
                      ? IMAGES.dinnerPlate
                      : tab === "platters"
                      ? IMAGES.platterBoard
                      : tab === "kids"
                      ? IMAGES.kidsPlate
                      : tab === "vegan"
                      ? IMAGES.veganPlate
                      : IMAGES.glutenFreePlate
                  }
                  alt="menu"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="bg-pink-soft p-8">
                <div className="overline mb-3">Chef's Note</div>
                <p className="text-sm text-navy/80 leading-relaxed">
                  Menus lean on what's fresh at the Bermuda markets each morning.
                  Expect thoughtful swaps, wild herbs, and the occasional
                  caught-that-day surprise. Allergies and dietary needs are
                  warmly accommodated — just tell us at booking.
                </p>
              </div>
              <div className="bg-white border border-[color:var(--am-line)] p-6 mt-4" data-testid="dietary-legend">
                <div className="overline mb-3">Dietary Key</div>
                <ul className="text-xs text-navy/80 space-y-1.5">
                  <li><strong className="font-serif-display text-base text-navy mr-2">VE</strong> Vegan</li>
                  <li><strong className="font-serif-display text-base text-navy mr-2">VG</strong> Vegetarian</li>
                  <li><strong className="font-serif-display text-base text-navy mr-2">GF</strong> Gluten-Free</li>
                  <li><strong className="font-serif-display text-base text-navy mr-2">GF*</strong> Gluten-Free on request</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
